import { useCallback } from 'react';
import { SprintAPI } from '../../utils/api';
import { calculateNextNodePosition } from '../../utils/nodePositioning';
import { 
  isTargetDescendantOfDragged,
  removeTaskLinkFromParent,
  dispatchCampaignNestedEvent,
  dispatchTasksUpdatedEvent,
  dispatchFocusMissionEvents,
} from './utils';
import { getMissionDragContext } from '../../utils/missionToSidebarBridge';
import { connectCampaignCard } from './connectCampaignCard';
import { dropOptimisticCampaignCard } from './optimisticCampaignCard';

/**
 * Hook for nesting a campaign inside another campaign.
 * 
 * Creates a new task node in the target campaign that links to the dragged campaign.
 * Handles:
 * - Circular reference validation
 * - Duplicate link prevention
 * - Old parent cleanup (if moving from one parent to another)
 * - Position calculation for the new node
 */
export const useNestCampaign = (setSprints) => {
  /**
   * Nest a campaign inside another campaign.
   * 
   * @param {string} draggedSprintId - ID of the campaign being nested
   * @param {string} targetSprintId - ID of the campaign to nest into
   * @param {string} missionName - Name for the new task node
   * @param {Object} options - Optional configuration
   * @param {Object} options.position - {x, y} position for the new node (used when dropping into flow view)
   * @param {string} options.oldParentSprintId - Fallback old parent to clean the link task
   *   from when the dragged sprint's parentSprintId is missing/stale (e.g. container-node
   *   drags from a view, where we know exactly which campaign the node came from)
   */
  const nestCampaign = useCallback(async (draggedSprintId, targetSprintId, missionName, options = {}) => {
    const abandonOptimistic = () => dropOptimisticCampaignCard(targetSprintId, options.existingTaskId);
    try {
    // ALWAYS fetch fresh data from API to avoid overwriting unsaved changes
    // This is critical because the user might have unsaved changes in SprintDetail
    const [targetSprint, draggedSprint] = await Promise.all([
      SprintAPI.get(targetSprintId),
      SprintAPI.get(draggedSprintId),
    ]);
    
    if (!targetSprint || !draggedSprint) {
      console.error('[NEST] Failed to fetch sprint data');
      abandonOptimistic();
      return;
    }
    
    // VALIDATION: Check if target is a descendant of the dragged campaign
    if (isTargetDescendantOfDragged(targetSprint, draggedSprintId)) {
      console.warn(`[NEST] Cannot nest "${draggedSprint.name}" into "${targetSprint.name}" - would create circular reference`);
      abandonOptimistic();
      return;
    }
    
    const ctx = getMissionDragContext();
    const liveTarget = ctx?.localSprint?.id === targetSprintId
      && !ctx?.isMultiCampaignMode
      && Array.isArray(ctx.localSprint.tasks)
      ? ctx.localSprint
      : null;

    // VALIDATION: Check if target already has a task linking to this campaign
    const existingTasks = liveTarget?.tasks || targetSprint.tasks || [];
    const existingLinkTask = existingTasks.find(t => t.nestedSprintId === draggedSprintId);
    
    if (existingLinkTask) {
      console.warn(`[NEST] Campaign "${draggedSprint.name}" is already linked in "${targetSprint.name}" via task "${existingLinkTask.name}". Skipping to prevent duplicate.`);
      // Just update the dragged sprint's parentSprintId if it's different
      if (draggedSprint.parentSprintId !== targetSprintId) {
        const updatedDraggedSprint = {
          ...draggedSprint,
          parentSprintId: targetSprintId,
          parentTaskId: existingLinkTask.id,
        };
        await SprintAPI.update(draggedSprintId, updatedDraggedSprint);
        setSprints(prev => prev.map(s => 
          s.id === draggedSprintId ? updatedDraggedSprint : s
        ));
        
        // Dispatch event so SprintDetail can update its localSprint if viewing the dragged sprint
        dispatchCampaignNestedEvent(draggedSprintId, targetSprintId, targetSprintId);
      }
      abandonOptimistic();
      return;
    }
    
    // CLEANUP: If the dragged campaign already has a parent, remove the old task link.
    // Fall back to the caller-provided old parent (container-node drags from a view)
    // when parentSprintId is missing or stale.
    let updatedOldParentSprint = null;
    let oldParentId = null;

    const knownOldParentId = draggedSprint.parentSprintId || options.oldParentSprintId || null;
    if (knownOldParentId && knownOldParentId !== targetSprintId) {
      oldParentId = knownOldParentId;
      // Prefer the live view's copy of the old parent over the server copy: mid-session
      // edits (task changes not yet flushed by the save debounce) live only in the view,
      // and saving a stale server copy over them would lose those edits.
      const ctx = getMissionDragContext();
      const liveOldParent =
        ctx?.localSprint?.id === oldParentId
        && !ctx?.isMultiCampaignMode
        && Array.isArray(ctx.localSprint.tasks)
        && Array.isArray(ctx.localSprint.categories)
          ? ctx.localSprint
          : null;
      updatedOldParentSprint = await removeTaskLinkFromParent(oldParentId, draggedSprintId, liveOldParent);
    }
    
    // A (+) confirm already inserted this card. Stamp the real campaign onto it
    // instead of adding a second one.
    const pending = options.existingTaskId
      ? existingTasks.find((task) => task.id === options.existingTaskId)
      : null;
    const positions = liveTarget?.nodePositions || targetSprint.nodePositions || {};

    let newTaskId;
    let newPosition;
    let tasksForSave;

    if (pending) {
      newTaskId = pending.id;
      const livePos = positions[pending.id];
      newPosition = livePos && Number.isFinite(livePos.x) && Number.isFinite(livePos.y)
        ? { x: livePos.x, y: livePos.y }
        : { x: pending.x, y: pending.y };
      tasksForSave = existingTasks.map((task) => {
        if (task.id !== pending.id) return task;
        const rest = { ...task };
        delete rest.creatingCampaign;
        delete rest._replacedSource;
        return {
          ...rest,
          name: missionName || draggedSprint.name,
          nestedSprintId: draggedSprintId,
        };
      });
    } else {
      newTaskId = options.existingTaskId || `task-${Date.now()}`;
      const sourceTask = options.connect?.sourceTaskId
        ? existingTasks.find((task) => task.id === options.connect.sourceTaskId)
        : null;
      const besideSource = sourceTask && Number.isFinite(sourceTask.x) && Number.isFinite(sourceTask.y)
        ? {
            x: sourceTask.x,
            y: sourceTask.y + (options.connect.mode === 'before' ? -180 : 180),
          }
        : null;
      newPosition = options.position
        ? { x: options.position.x, y: options.position.y }
        : (besideSource || calculateNextNodePosition(existingTasks, positions));

      const draftedTask = {
        id: newTaskId,
        name: missionName || draggedSprint.name,
        status: 'pending',
        nestedSprintId: draggedSprintId,
        createdAt: new Date().toISOString(),
        x: newPosition.x,
        y: newPosition.y,
      };
      const { tasks: wiredTasks, task: newTask } = connectCampaignCard(existingTasks, draftedTask, options.connect);
      tasksForSave = [...wiredTasks, newTask];
    }
    
    // Update the dragged sprint to have the target as parent
    const updatedDraggedSprint = {
      ...draggedSprint,
      parentSprintId: targetSprintId,
      parentTaskId: newTaskId,
    };
    
    // Update the target sprint with the new task
    // Also add the dragged campaign to the end of childOrder for sidebar ordering
    const existingChildOrder = targetSprint.childOrder || [];
    const newChildOrder = existingChildOrder.includes(draggedSprintId)
      ? existingChildOrder
      : [...existingChildOrder, draggedSprintId];
    
    const updatedTargetSprint = {
      ...targetSprint,
      ...(liveTarget ? { categories: liveTarget.categories || targetSprint.categories } : {}),
      tasks: tasksForSave,
      nodePositions: {
        ...positions,
        [newTaskId]: newPosition,
      },
      childOrder: newChildOrder,
    };
    
    // Build array of API updates
    const apiUpdates = [
      SprintAPI.update(targetSprintId, updatedTargetSprint),
      SprintAPI.update(draggedSprintId, updatedDraggedSprint),
    ];
    
    // If we need to clean up old parent, add that update too
    if (updatedOldParentSprint) {
      apiUpdates.push(SprintAPI.update(oldParentId, updatedOldParentSprint));
    }
    
    // Save all sprints to API
    await Promise.all(apiUpdates);
    
    // Update local state
    setSprints(prev => prev.map(s => {
      if (s.id === targetSprintId) return updatedTargetSprint;
      if (s.id === draggedSprintId) return updatedDraggedSprint;
      if (updatedOldParentSprint && s.id === oldParentId) return updatedOldParentSprint;
      return s;
    }));
    
    // Dispatch events for state sync
    dispatchCampaignNestedEvent(draggedSprintId, targetSprintId, targetSprintId);
    dispatchTasksUpdatedEvent(targetSprintId, updatedTargetSprint.tasks, updatedTargetSprint.nodePositions);
    
    if (updatedOldParentSprint) {
      dispatchTasksUpdatedEvent(oldParentId, updatedOldParentSprint.tasks, updatedOldParentSprint.nodePositions);
    }
    
    // Focus on the new mission (skip if the user dropped it, or (+) already placed it)
    dispatchFocusMissionEvents(newTaskId, !!options.position || !!options.connect);

    // The optimistic card already tidied. Tidying again would shift the board
    // a second time once the campaign finishes.
    if (options.connect && !options.existingTaskId) {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('flowchartTidyGraph'));
      }, 50);
    }
    
    console.log(`[NEST] Successfully nested "${draggedSprint.name}" into "${targetSprint.name}"`);
    } catch (error) {
      abandonOptimistic();
      throw error;
    }
  }, [setSprints]);

  return { nestCampaign };
};

export default useNestCampaign;
